import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {localizeRole, Role} from '../../model/auth/Role';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {NotificationHandler} from '../../helper/notification.handler';
import {TrackingService} from '../../service/tracking.service';
import {CertificateService} from '../../service/certificate.service';
import {
  Certificate,
  CertificateDto,
  CertificateType,
  TestCertificateData,
  UserCertificateType
} from '../../model/contest/Certificate';
import {localizeUserExamGrade, UserExamGrade} from '../../model/stats/Stats';
import {AssetType} from '../../model/asset/Asset';
import {AssetService} from '../../service/asset.service';
import {Observable, of} from 'rxjs';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';

export interface ContestCertificateModalData {
  competitionId: string;
  contestId: string;
}

@Component({
  selector: 'app-contest-certificate-modal',
  templateUrl: './contest-certificate-modal.component.html',
  styleUrls: ['./contest-certificate-modal.component.scss'],
})
export class ContestCertificateModalComponent extends BaseModalComponent {

  readonly roles = [Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  fontSizeField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(150)
  ]);
  x1Field = new FormControl('', [
    Validators.required, Validators.min(0)
  ]);
  y1Field = new FormControl('', [
    Validators.required, Validators.min(0)
  ]);
  x2Field = new FormControl('', [
    Validators.required, Validators.min(0)
  ]);
  y2Field = new FormControl('', [
    Validators.required, Validators.min(0)
  ]);
  secondaryFontSizeField = new FormControl('', [
    Validators.min(1), Validators.max(150)
  ]);
  secondaryX1Field = new FormControl('', [
    Validators.min(0)
  ]);
  secondaryY1Field = new FormControl('', [
    Validators.min(0)
  ]);
  secondaryX2Field = new FormControl('', [
    Validators.min(0)
  ]);
  secondaryY2Field = new FormControl('', [
    Validators.min(0)
  ]);

  primaryTextField = new FormControl('', [
    Validators.required, Validators.minLength(1), Validators.maxLength(256)
  ]);
  secondaryTextField = new FormControl({value: '', disabled: this.secondaryConfigIsEmpty}, [
    Validators.minLength(1), Validators.maxLength(256)
  ]);

  optionsForm = new FormGroup({
    fontSize: this.fontSizeField,
    x1: this.x1Field,
    y1: this.y1Field,
    x2: this.x2Field,
    y2: this.y2Field,
    secondaryFontSize: this.secondaryFontSizeField,
    secondaryX1: this.secondaryX1Field,
    secondaryY1: this.secondaryY1Field,
    secondaryX2: this.secondaryX2Field,
    secondaryY2: this.secondaryY2Field,
  });

  testForm = new FormGroup({
    primary: this.primaryTextField,
    secondary: this.secondaryTextField
  });

  editMode = false;
  createMode = false;

  prevImage?: string;
  file?: File;

  readonly contestId?: string;
  readonly competitionId?: string;
  type?: CertificateType;

  certificate?: Certificate;
  testCert?: Certificate;

  constructor(private assetService: AssetService,
              private certificateService: CertificateService,
              private trackingService: TrackingService,
              private dialog: MatDialog,
              dialogRef: MatDialogRef<ContestCertificateModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: ContestCertificateModalData) {
    super(dialogRef, progressService);

    if (data.contestId) this.contestId = data.contestId;
    if (data.competitionId) this.competitionId = data.competitionId;
  }

  initContent() {
  }

  private loadCertificate(type: CertificateType) {
    if (!this.contestId || !type) return;

    this.startProgress();
    this.certificate = undefined;
    this.testCert = undefined;

    this.certificateService.getCertificate(this.contestId, type).subscribe({
      next: certificate => {
        this.certificate = certificate;

        this.setStoredValues(this.certificate);

        this.toggleEditMode(false);

        this.stopProgress();
      }, error: () => {
        this.certificate = new Certificate(this.type);
        this.setStoredValues(this.certificate);
        this.fontSizeField.setValue(96);
        this.toggleEditMode(true);
        this.createMode = true;

        NotificationHandler.notification($localize`:Notifications|Certificate details were not found message@@notifications-certificate-details-not-found-message:Certificate was not found, you can create one`);
        this.stopProgress();
      }
    });
  }

  setStoredValues(certificate: Certificate) {
    if (certificate.primary_config) {
      this.fontSizeField.setValue(certificate.primary_config.font_size);
      this.x1Field.setValue(certificate.primary_config.x1);
      this.y1Field.setValue(certificate.primary_config.y1);
      this.x2Field.setValue(certificate.primary_config.x2);
      this.y2Field.setValue(certificate.primary_config.y2);
    } else {
      this.fontSizeField.setValue(undefined);
      this.x1Field.setValue(undefined);
      this.y1Field.setValue(undefined);
      this.x2Field.setValue(undefined);
      this.y2Field.setValue(undefined);
    }
    if (certificate.secondary_config) {
      this.secondaryFontSizeField.setValue(certificate.secondary_config.font_size);
      this.secondaryX1Field.setValue(certificate.secondary_config.x1);
      this.secondaryY1Field.setValue(certificate.secondary_config.y1);
      this.secondaryX2Field.setValue(certificate.secondary_config.x2);
      this.secondaryY2Field.setValue(certificate.secondary_config.y2);
    } else {
      this.secondaryFontSizeField.setValue(undefined);
      this.secondaryX1Field.setValue(undefined);
      this.secondaryY1Field.setValue(undefined);
      this.secondaryX2Field.setValue(undefined);
      this.secondaryY2Field.setValue(undefined);
    }
  }

  toggleEditMode(editMode: boolean) {
    this.editMode = editMode;

    if (this.editMode) {
      this.fontSizeField.enable();
      this.x1Field.enable();
      this.y1Field.enable();
      this.x2Field.enable();
      this.y2Field.enable();

      this.secondaryFontSizeField.enable();
      this.secondaryX1Field.enable();
      this.secondaryY1Field.enable();
      this.secondaryX2Field.enable();
      this.secondaryY2Field.enable();

      this.primaryTextField.disable();
      this.secondaryTextField.disable();
    } else {
      this.fontSizeField.disable();
      this.x1Field.disable();
      this.y1Field.disable();
      this.x2Field.disable();
      this.y2Field.disable();

      this.secondaryFontSizeField.disable();
      this.secondaryX1Field.disable();
      this.secondaryY1Field.disable();
      this.secondaryX2Field.disable();
      this.secondaryY2Field.disable();

      this.primaryTextField.enable();
      if (!this.secondaryConfigIsEmpty) {
        this.secondaryTextField.enable();
      } else {
        this.secondaryTextField.disable();
      }
    }
  }

  compareTypes(t1?: CertificateType, t2?: CertificateType): boolean {
    return t1 == t2;
  }

  loadTypes = (): Observable<CertificateType[]> => {
    return of([
      UserExamGrade.excellent,
      UserExamGrade.good,
      UserExamGrade.average,
      UserCertificateType.teacher,
    ]);
  };

  displayType(type: CertificateType): string {
    return type == UserCertificateType.teacher
      ? localizeRole(Role.Teacher)
      : localizeUserExamGrade(type);
  }

  onFileSelected(files: File[]) {
    this.file = files && files.length > 0 ? files[0] : undefined;
  }

  handleCertificateTypeChange(type: CertificateType) {
    this.type = type;
    this.loadCertificate(this.type);
  }

  get secondaryConfigValid(): boolean {
    return parseInt(this.secondaryFontSizeField.value as string) > 0
      && parseInt(this.secondaryX1Field.value as string) >= 0
      && parseInt(this.secondaryY1Field.value as string) >= 0
      && parseInt(this.secondaryX2Field.value as string) >= 0
      && parseInt(this.secondaryY2Field.value as string) >= 0;
  }

  get secondaryConfigIsEmpty(): boolean {
    return !(this.secondaryFontSizeField.value as string)
      && !(this.secondaryX1Field.value as string)
      && !(this.secondaryY1Field.value as string)
      && !(this.secondaryX2Field.value as string)
      && !(this.secondaryY2Field.value as string);
  }

  proceedAllowed(): boolean {
    return this.optionsForm.valid
      && (!!this.file || !!this.certificate?.url)
      && (this.secondaryConfigIsEmpty || this.secondaryConfigValid);
  }

  proceed(): void {
    if (!this.contestId || !this.type) return;

    this.startProgress();

    if (this.file) {
      this.assetService.registerAssetUpload(AssetType.certificate, this.file).subscribe({
        next: asset => {
          if (asset.uploadUrl && this.file) this.assetService.uploadContent(asset.uploadUrl, this.file).subscribe(() => {
            this.saveCertificate(asset.url || '');
          }, error => {
            NotificationHandler.handleError($localize`:Notifications|Asset upload failed message@@notifications-asset-upload-failed-error:Asset upload has failed`, error);
            this.stopProgress();
          });
        }, error: error => {
          NotificationHandler.handleError($localize`:Notifications|Asset upload registration failed message@@notifications-asset-upload-registration-failed-error:Asset upload registration has failed`, error);
          this.stopProgress();
        }
      });
    } else {
      this.saveCertificate(this.certificate?.url || '');
    }
  }

  private saveCertificate(url: string) {
    if (this.contestId && this.type) {
      const certificate: CertificateDto = {
        url: url,
        primary_config: {
          font_size: parseInt(this.fontSizeField.value as string),
          x1: parseInt(this.x1Field.value as string),
          y1: parseInt(this.y1Field.value as string),
          x2: parseInt(this.x2Field.value as string),
          y2: parseInt(this.y2Field.value as string),
        }
      };

      if (!this.secondaryConfigIsEmpty) {
        certificate.secondary_config = {
          font_size: parseInt(this.secondaryFontSizeField.value as string),
          x1: parseInt(this.secondaryX1Field.value as string),
          y1: parseInt(this.secondaryY1Field.value as string),
          x2: parseInt(this.secondaryX2Field.value as string),
          y2: parseInt(this.secondaryY2Field.value as string),
        };
      }

      this.certificateService.saveCertificate(this.contestId, this.type, certificate).subscribe({
        next: certificate => {
          this.certificate = certificate;

          this.toggleEditMode(false);
          this.createMode = false;

          this.stopProgress();
        }, error: error => {
          NotificationHandler.handleError($localize`:Notifications|Certificate save failed message@@notifications-certificate-save-failed-error:Certificate save has failed`, error);
          this.stopProgress();
        }
      });
    }
  }

  testCertificate() {
    if (!this.contestId || !this.type) return;

    this.testCert = undefined;
    this.startProgress();

    const data: TestCertificateData = {
      primary_text: this.primaryTextField.value as string
    };
    if (!!this.secondaryTextField.value) data.secondary_text = this.secondaryTextField.value as string;

    this.certificateService.testCertificate(this.contestId, this.type, data).subscribe({
      next: certificate => {
        this.testCert = certificate;
        this.stopProgress();
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|Certificate testing failed message@@notifications-certificate-testing-failed-error:Certificate testing has failed`, error);
        this.stopProgress();
      }
    });
  }

  cancelEdit() {
    if (this.createMode) {
      this.certificate = undefined;
      this.type = undefined;
      this.createMode = false;
      this.editMode = false;
    } else {
      if (this.certificate) {
        this.setStoredValues(this.certificate);
        if (this.prevImage) this.certificate.url = this.prevImage;
      }
      this.prevImage = undefined;
      this.toggleEditMode(false);
    }
  }

  deleteCertificate(certificate: Certificate) {
    if (!certificate || !certificate.type) return;

    const data: DeleteConfirmationData = {
      title: $localize`:Contest Certificate Modal|Delete Certificate prompt title@@contest-certificate-modal-certificate-delete-certificate-prompt-title:Certificate Delete`,
      itemType: $localize`:General|Contest certificate term@@general-contest-certificate-term:Certificate`,
      name: this.displayType(certificate.type),
      actionDesc: $localize`:Contest Certificate Modal|Delete Certificate prompt@@contest-certificate-modal-certificate-delete-certificate-prompt:will be removed from the system and users would not be able to request this type of certificate.`,
      archive: false
    };
    const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
      if (this.contestId && this.type) this.certificateService.deleteCertificate(this.contestId, this.type).subscribe({
        next: () => {
          context.modal.deleteHandled();

          this.certificate = new Certificate(this.type);
          this.setStoredValues(this.certificate);
          this.fontSizeField.setValue(96);
          this.toggleEditMode(true);
          this.createMode = true;
        },
        error: error => context.modal.deleteHandled($localize`:Notifications|Certificate delete has failed message@@notifications-certificate-delete-failed-error:Certificate delete has failed`, error)
      });
    });
  }
}
